/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import Grid from '../../../../components/Grid/Grid';
import Button from '../../../../components/Button/Button';
import InputField from '../../../../components/InputField/InputField';
import Dialog from '../../../../components/Dialog/Dialog';
import DialogTitle from '../../../../components/DialogTitle/DialogTitle';
import useForm from '../../../../hooks/useForm';
import DialogContent from '../../../../components/DialogContent/DialogContent';
import { addMaterialType, getMaterialTypeList } from '../../../../services/settings-config';
import useCancelToken from '../../../../hooks/useCancelToken';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import Typography from '../../../../components/Typography/Typography';
import useStyles from '../../useStyles';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import MaterialTypeConfigRow from './MaterialTypeConfigRow';

const MaterialTypeUpdateDialog = ({ isOpen, onClose, targetRow }) => {
  const { classes } = useStyles();
  const { newCancelToken } = useCancelToken();
  const initialForm = {
    materialTypeName: '',
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [materialTypeList, setMaterialTypeList] = useState([]);
  const [materialTypeLoading, setMaterialTypeLoading] = useState(false);
  const [materialTypeError, setMaterialTypeError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [showStatus, setShowStatus] = useState(false);
  const snackbarTimer = useRef(null);

  const openSnackbar = () => {
    setShowStatus(true);
    clearTimeout(snackbarTimer.current);
    snackbarTimer.current = setTimeout(() => {
      setShowStatus(false);
      clearTimeout(snackbarTimer.current);
    }, 3000);
  };

  const validationForm = () => {
    if (formData.materialTypeName.length === 0) {
      setFormErr({ ...formErr, materialTypeName: '請輸入正確材質名稱' });
      return false;
    }
    return true;
  };

  const handleClose = () => {
    resetFormErr();
    setFormData(initialForm);
    setMaterialTypeList([]);
    setMaterialTypeError('');
    setMaterialTypeLoading(false);
    onClose();
  };

  const handleGetMaterialTypeList = async () => {
    const cancelToken = newCancelToken();
    setMaterialTypeLoading(true);
    setMaterialTypeError('');
    await getMaterialTypeList({
      data: {
        materialId: targetRow.materialId,
      },
      cancelToken,
      onSuccess: (result) => {
        setMaterialTypeList(
          result.materialTypeConfigList.map((config) => ({
            ...config,
            rowId: config.materialTypeId,
            isEnabled: config.isActive,
          }))
        );
      },
      onError: (error) => {
        setMaterialTypeError(error);
      },
      onSettled: () => {
        setMaterialTypeLoading(false);
      },
    });
  };

  const handleAddMaterialType = () => {
    setIsCreateMode(true);
  };

  const handleSubmitAddMaterialType = async () => {
    if (!validationForm()) return;
    const cancelToken = newCancelToken();
    setIsSubmitting(true);
    await addMaterialType({
      data: {
        materialId: targetRow.materialId,
        materialTypeName: formData.materialTypeName,
      },
      cancelToken,
      onSuccess: () => {
        setIsCreateMode(false);
        openSnackbar();
        setStatusType('success');
        setStatusMsg('新增材質類型成功！');
        resetFormErr();
        setFormData(initialForm);
        handleGetMaterialTypeList();
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        openSnackbar();
        setStatusType('error');
        setStatusMsg(
          noPrivilegeMsg || `新增材質類型失敗！請確認網路是否正常或聯繫軟體部門：${result}`
        );
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  const handleSaveMaterialType = () => {
    openSnackbar();
    setStatusType('success');
    setStatusMsg('更新材質類型成功！');
    handleGetMaterialTypeList();
  };

  const handleSaveMaterialTypeError = (result, noPrivilegeMsg) => {
    openSnackbar();
    setStatusType('error');
    setStatusMsg(noPrivilegeMsg || `儲存材質類型失敗！請確認網路是否正常或聯繫軟體部門：${result}`);
  };

  const handleCancelAddMaterialType = () => {
    setIsCreateMode(false);
    setFormData(initialForm);
  };

  const handleDeleteMaterialType = () => {
    openSnackbar();
    setStatusType('success');
    setStatusMsg('刪除材質類型成功！');
    handleGetMaterialTypeList();
  };

  const handleDeleteMaterialTypeError = (result, noPrivilegeMsg) => {
    openSnackbar();
    setStatusType('error');
    setStatusMsg(noPrivilegeMsg || `刪除材質類型失敗！請確認網路是否正常或聯繫軟體部門：${result}`);
  };

  useEffect(() => {
    if (isOpen) {
      handleGetMaterialTypeList();
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={(_e, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="update-material-type-title">{`${targetRow.materialName} 種類列表`}</DialogTitle>
      <DialogContent dividers sx={{ paddingTop: 2, paddingBottom: 3 }}>
        <Grid container direction="column" spacing={2}>
          <Grid
            item
            container
            direction="column"
            xs={12}
            sx={
              materialTypeLoading || materialTypeError || materialTypeList.length > 0
                ? { paddingTop: 3, paddingBottom: 3 }
                : {}
            }
          >
            <Snackbar
              msg={statusMsg}
              type={statusType}
              open={showStatus}
              className={classes.snackbar}
            />
            {materialTypeLoading && <CircularProgress />}
            {!materialTypeLoading && materialTypeError && (
              <Typography variant="body1" color="error" textAlign="center">
                {materialTypeError}
              </Typography>
            )}
            <Grid
              container
              item
              xs={12}
              spacing={2}
              direction="column"
              sx={{
                paddingLeft: 2,
                paddingRight: 2,
                overflowY: 'scroll',
                height: '100%',
                flexWrap: 'nowrap',
              }}
            >
              <Grid container item xs={12} justifyContent="flex-start" sx={{ py: 3 }}>
                {isCreateMode ? (
                  <>
                    <Grid item xs={6}>
                      <InputField
                        id="materialTypeName"
                        size="small"
                        label="種類名稱"
                        placeholder="請輸入種類名稱"
                        type="text"
                        value={formData.materialTypeName}
                        variant="outlined"
                        onChange={(e) =>
                          setFormData({ ...formData, materialTypeName: e.target.value })
                        }
                        error={formErr.materialTypeName}
                        errormsg={formErr.materialTypeName}
                        required
                      />
                    </Grid>
                    <Grid
                      item
                      container
                      xs={2}
                      alignItems="flex-start"
                      gap={1}
                      sx={{ marginTop: 1.1 }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleSubmitAddMaterialType}
                        size="small"
                        sx={{ backgroundColor: 'white', height: 32 }}
                        disabled={isSubmitting}
                      >
                        <Typography variant="body1">新增</Typography>
                      </Button>
                      <Button
                        variant="outlined"
                        color="grey"
                        onClick={handleCancelAddMaterialType}
                        size="small"
                        sx={{ backgroundColor: 'white', height: 32 }}
                        disabled={isSubmitting}
                      >
                        <Typography variant="body1">取消</Typography>
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleAddMaterialType}
                    sx={{ backgroundColor: 'white', borderRadius: '100px' }}
                    startIcon={<AddIcon />}
                  >
                    新增一項
                  </Button>
                )}
              </Grid>
              <Grid
                container
                item
                xs={12}
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  padding: 0,
                  paddingTop: 0.6,
                  paddingBottom: 0.6,
                  marginBottom: 0.6,
                  marginTop: 0.6,
                })}
              >
                <Grid item xs={8}>
                  <Typography variant="subtitle2" fontWeight="medium">
                    種類
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" textAlign="right" fontWeight="medium">
                    Action
                  </Typography>
                </Grid>
              </Grid>
              {materialTypeList.length === 0 && (
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ py: 3 }}
                >
                  <Typography variant="body1" textAlign="center">
                    目前沒有任何種類，請新增一項種類。
                  </Typography>
                </Grid>
              )}
              {materialTypeList.map((item) => (
                <MaterialTypeConfigRow
                  key={item.rowId}
                  configRow={item}
                  onSaveMaterialType={handleSaveMaterialType}
                  onSaveMaterialTypeError={handleSaveMaterialTypeError}
                  onDeleteMaterialType={handleDeleteMaterialType}
                  onDeleteMaterialTypeError={handleDeleteMaterialTypeError}
                  targetMaterial={targetRow}
                />
              ))}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Grid container item xs={12} justifyContent="flex-end" sx={{ padding: 2 }}>
        <Button variant="contained" color="primary" onClick={handleClose}>
          關閉
        </Button>
      </Grid>
    </Dialog>
  );
}; // NewQCTypeAction
MaterialTypeUpdateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  targetRow: PropTypes.object.isRequired,
};

export default MaterialTypeUpdateDialog;
